//@flow
import React, {Fragment, useEffect, useState} from 'react';
import Button from "../../components/button/button";
import {Link} from "react-router-dom";
import {getRouteUrl} from "../../helpers/getRouteUrl";
import {ROUTE_PAGE_ORDERS} from "../../routers/routes";
import Icon from "../../components/icon/icon";
import Images from "../../assets/images";
import Util from "../../components/util/util";
import Title from "../../components/title/title";
import Divider from "../../components/divider/divider";
import Preview from "../../components/preview/preview";
import "./template.css"
import BillingAndShippingInfo from "../../components/order/partials/billingAndShippingInfo/billingAndShippingInfo";
import useOrderAction from "../../hooks/order/useOrderAction";
import {useParams} from "react-router";
import useOrderDetailsData from "../../hooks/order/useOrderDetailsData";
import {calculateProductTax, categorizeProducts} from "../../helpers/helpers";
import {useTranslation} from "react-i18next";
import {PAGE_ORDERS} from "../../locales/pages/namespaces";
import Loading from "../../components/loading/loading";
import {useOrderData} from "../../hooks/order/useOrderData";
import OrderType from "../../components/orderType/orderType";
import CloneOrderModal from "../../components/cloneOrderModal/cloneOrderModal";
import useOrderCreate from "../../hooks/order/useOrderCreate";
import ExistingOrderModal from "../../components/existingOrderModal/existingOrderModal";
import {useUserData} from "../../hooks/user/useUserData";
import {useHasPermission} from "../../modules/permission/hooks/useHasPermission";
import {Permissions} from "../../helpers/constants";

const View = ({template}) => {
    const {t} = useTranslation(PAGE_ORDERS)
    const {price, title, notes, billing, shipping, products, type, typeDescription, id, orderSheetIsInternal} = template
    const categorizedProducts = categorizeProducts(products)
    const tax = calculateProductTax(products).toFixed(2)
    const {data} = useUserData()
    const isAdmin = useHasPermission([Permissions.AccessAdmin], {shouldView: true})
    const isSalesPerson = useHasPermission([Permissions.AccessSalesPerson], {shouldView: true})
    const {cloneOrder} = useOrderAction()
    const [showCloneOrderModal, setShowCloneOrderModal] = useState(false)
    const {activeOrderOfType, showActiveOrderModal, setShowActiveOrderModal} = useOrderCreate(type)

    const onRepeatOrderClick = () => {
        if (!activeOrderOfType) {
            cloneOrder(id)
            setShowCloneOrderModal(true)
            return
        }
        setShowActiveOrderModal(true)
    }

    return (
        <Fragment>
            <Util align={'flex-start'}>
                <div>
                    <Title>
                        {title?.toUpperCase()}
                    </Title>
                    <Divider spacingBottom={1}/>
                    <Util>
                        <Title variant={"h3"}>
                            {t('total_cost')} {t('with_tax')}
                        </Title>
                        <Title variant={"h3"}>
                            {price}{data?.currencySymbol}
                        </Title>
                    </Util>
                    <Divider spacingTop={1}/>
                </div>
                <div className={'info'}>
                    <Util align={'baseline'}>
                        <strong>{t('Type')}: </strong>
                        <OrderType type={type} typeDescription={typeDescription}/>
                    </Util>
                </div>
            </Util>
            <br/>
            {Object
                .keys(categorizedProducts)
                .map(category => {
                    const products = categorizedProducts[category];
                    return (
                        <Fragment key={category}>
                            <br/>
                            <Title style={{marginBottom: '.5rem'}} variant={"h4"}>
                                {category}
                            </Title>
                            {products.map((product) => (
                                <Preview
                                    orderId={id}
                                    key={product.id}
                                    product={product}
                                    disabled
                                    orderType={type}
                                />
                            ))}
                        </Fragment>
                    );
                })}
            <br/>
            <div className="order-page__bottom">
                <div className="order-page__bottom_left">
                    {notes && (
                        <div className={"order-page__notes"}>
                            <Title variant={"h4"}>
                                {t('notes')}
                            </Title>
                            <div dangerouslySetInnerHTML={{__html: notes}}/>
                        </div>
                    )}
                    <br/>
                    {billing && shipping && (
                        <BillingAndShippingInfo
                            billing={billing}
                            shipping={shipping}
                        />
                    )}
                </div>
                <div className="order-page__bottom_right">
                    <Util>
                        <Title variant={"h4"}>
                            {t('total_cost')} {t('with_tax')}
                        </Title>
                        <Title variant={"h4"} subtitle={t('tax') + ' (' + tax + data?.currencySymbol + ')'}>
                            {price}{data?.currencySymbol}
                        </Title>
                    </Util>
                    <Divider spacingTop={1} spacingBottom={1}/>
                    {((orderSheetIsInternal && (isAdmin || isSalesPerson)) || (!orderSheetIsInternal && !isSalesPerson)) && (
                        <Button.Group>
                            <Button onClick={onRepeatOrderClick}>
                                {t('repeat_order')}
                            </Button>
                        </Button.Group>
                    )}
                </div>
            </div>
            <CloneOrderModal
                hideActions
                createTemplate={false}
                show={showCloneOrderModal}
                onClose={() => setShowCloneOrderModal(false)}
                orderId={id}
            />
            <ExistingOrderModal
                type={type}
                show={showActiveOrderModal}
                onClose={() => setShowActiveOrderModal(false)}
            />
        </Fragment>
    )
}

const Template = () => {
    const {loading} = useOrderData()
    const {t} = useTranslation(PAGE_ORDERS)
    const {id} = useParams()
    const {getOrderDetails} = useOrderAction()
    const {order: template} = useOrderDetailsData(id)

    useEffect(() => {
        getOrderDetails(id)
    }, [getOrderDetails, id])

    return (
        <div className={'bg-container contained-page order-page'}>
            <Loading show={loading}/>
            <Button variant={'link'} component={Link} to={getRouteUrl(ROUTE_PAGE_ORDERS)}>
                <Icon icon={Images.icons.arrowLeft}/> {t('back')}
            </Button>
            {template && <View template={template}/>}
        </div>
    );
};

export default Template;